/**
 * Helpers for when the app runs as an installed PWA (home-screen icon).
 * In standalone mode there is no browser chrome — no URL bar, back button,
 * or tabs — so `window.open(..., "_blank")` and `<a target="_blank">`
 * trap the user in a dead-end view.
 */

/** True when running as an installed PWA (iOS or Android/desktop). */
export function isStandalone(): boolean {
    if (typeof window === "undefined") return false;
    const mq = window.matchMedia?.("(display-mode: standalone)");
    if (mq?.matches) return true;
    // Legacy iOS Safari home-screen flag.
    return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Normalize a user/DB-supplied external URL so it opens the real destination
 * directly. Handles two common breakages:
 *   - Google redirect wrappers (`https://www.google.com/url?q=…`) that get
 *     pasted in from Gmail/Docs and bounce through a blank google.com page.
 *   - Missing scheme (`www.example.com`) which would otherwise resolve
 *     relative to the app origin.
 */
export function normalizeExternalUrl(href: string): string {
    if (!href) return href;
    let url = href.trim();
    try {
        const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`;
        const parsed = new URL(withScheme);
        const host = parsed.hostname.toLowerCase();
        if ((host === "google.com" || host === "www.google.com") && parsed.pathname === "/url") {
            const target = parsed.searchParams.get("q") || parsed.searchParams.get("url");
            if (target) return normalizeExternalUrl(target);
        }
        url = withScheme;
    } catch {
        // Leave the original href untouched if it can't be parsed.
        return href;
    }
    return url;
}

/**
 * Open an external URL. In standalone, navigate in the same window so the
 * user can return via the system back gesture / app history. On desktop,
 * open a new tab.
 */
export function openExternalLink(href: string): void {
    if (!href) return;
    const target = normalizeExternalUrl(href);
    if (isStandalone()) {
        window.location.assign(target);
    } else {
        window.open(target, "_blank", "noopener,noreferrer");
    }
}
